import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  X,
  Copy,
  Spinner,
  ArrowsClockwise,
  Storefront,
  CheckCircle,
  Package,
  Lightbulb,
  PencilLine,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { downloadFromApi } from '@/utils/downloadBlob';

/**
 * EbookSalesBundleModal
 * ---------------------
 * Shown after the ebook is generated. On open it calls
 *   POST /api/ebook/{ebook_id}/sales-bundle
 * and displays each section with a copy-to-clipboard button so the creator
 * can paste into Stan Store, Gumroad, Shopify, email, or social.
 *
 * After the user is done, they never return to the dashboard — the footer
 * CTAs push them forward into the Content flow (Find Viral Ideas → Create
 * Content → Plan My Week). This keeps users inside the build loop.
 */
export default function EbookSalesBundleModal({
  isOpen,
  onClose,
  ebookId,
  ebookTitle,
  onFindViralIdeas,
  onCreateContent,
}) {
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [exportingZip, setExportingZip] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    if (isOpen && ebookId && !bundle) {
      fetchBundle(false);
    }
    if (!isOpen) {
      setBundle(null);
      setCopiedKey(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ebookId]);

  const fetchBundle = async (force) => {
    if (force) setRegenerating(true);
    else setLoading(true);
    try {
      const res = await api.post(
        `/ebook/${ebookId}/sales-bundle`,
        { force_regenerate: !!force },
        { timeout: 120000 },
      );
      setBundle(res.data);
      if (force) toast.success('Fresh sales bundle generated');
    } catch (err) {
      console.error('Sales bundle fetch failed', err);
      toast.error(err.response?.data?.detail || "Couldn't generate sales bundle. Please try again.");
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  };

  const copyToClipboard = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1800);
    } catch {
      toast.error("Couldn't copy — please select and copy manually");
    }
  };

  if (!isOpen) return null;

  const sections = bundle
    ? [
        { key: 'product_title', label: 'Product Title', value: bundle.product_title, hint: 'Use on Stan Store / Gumroad / Shopify product page' },
        { key: 'tagline', label: 'Tagline', value: bundle.tagline, hint: 'Use under the product title or as a banner' },
        { key: 'short_description', label: 'Short Description', value: bundle.short_description, hint: 'Stan Store thumbnail / meta description (≤180 chars)' },
        { key: 'long_description', label: 'Long Description', value: bundle.long_description, multiline: true, hint: 'Gumroad / Shopify product description' },
        {
          key: 'bullet_benefits',
          label: 'Bullet Benefits',
          value: Array.isArray(bundle.bullet_benefits) ? bundle.bullet_benefits.map((b) => `• ${b}`).join('\n') : '',
          multiline: true,
          hint: 'Paste as bullets on any product page',
        },
        { key: 'social_caption', label: 'Social Caption', value: bundle.social_caption, multiline: true, hint: 'Instagram / TikTok launch post' },
        { key: 'email_subject', label: 'Email Subject', value: bundle.email_subject, hint: 'Subject line for your launch email' },
        { key: 'email_body', label: 'Email Body', value: bundle.email_body, multiline: true, hint: 'Launch email to your list' },
      ]
    : [];

  const copyAll = async () => {
    if (!bundle) return;
    const blocks = sections.map((s) => `== ${s.label} ==\n${s.value || ''}`).join('\n\n');
    await copyToClipboard('__all__', blocks);
  };

  const downloadStorefrontZip = async () => {
    if (!ebookId) return;
    setExportingZip(true);
    const safe = (ebookTitle || 'ebook').replace(/[^a-z0-9._-]/gi, '_');
    try {
      const res = await downloadFromApi(
        api,
        `/ebook/${ebookId}/storefront-bundle`,
        `${safe}_storefront_bundle.zip`,
        { method: 'POST', data: {}, mime: 'application/zip' },
      );
      if (res.ios) {
        toast.success("Bundle opened — tap Share → Save to Files.", { duration: 6000 });
      } else {
        toast.success('Storefront bundle downloaded');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't build storefront bundle. Please try again.");
    } finally {
      setExportingZip(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 sm:p-6"
      data-testid="ebook-sales-bundle-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-3xl paygbot-modal-shell bg-gradient-to-br from-slate-900 to-slate-950 border border-white/10 rounded-2xl shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-br from-slate-900/95 to-slate-950/95 backdrop-blur-sm border-b border-white/10 p-4 sm:p-5 flex items-center justify-between">
          <div className="flex items-center space-x-3 min-w-0">
            <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500">
              <Storefront size={20} className="text-white" weight="fill" />
            </div>
            <div className="min-w-0">
              <h2 className="text-white text-lg font-bold">Sales Bundle</h2>
              <p className="text-slate-400 text-xs truncate">
                Ready-to-paste copy for <span className="text-slate-300">{ebookTitle || 'your ebook'}</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-slate-400"
            data-testid="ebook-sales-bundle-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4">
          {loading && (
            <div className="py-16 flex flex-col items-center justify-center space-y-3" data-testid="ebook-sales-bundle-loading">
              <Spinner size={40} className="text-emerald-400 animate-spin" />
              <p className="text-white font-medium">Writing your sales copy…</p>
              <p className="text-slate-400 text-xs text-center max-w-sm">
                Pulling from your ebook&apos;s title, chapters, and business memory to draft the full bundle.
              </p>
            </div>
          )}

          {!loading && bundle && (
            <>
              <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-start space-x-3">
                <CheckCircle size={20} className="text-emerald-400 flex-shrink-0 mt-0.5" weight="fill" />
                <p className="text-emerald-100 text-sm leading-relaxed">
                  Every section below is sale-ready. Copy what you need and paste it straight into
                  Stan Store, Gumroad, Shopify, your email tool or social post.
                </p>
              </div>

              {/* Action bar */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={copyAll}
                  className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg flex items-center space-x-1.5 transition-colors"
                  data-testid="ebook-sales-bundle-copy-all"
                >
                  <Copy size={13} />
                  <span>{copiedKey === '__all__' ? 'Copied!' : 'Copy all'}</span>
                </button>
                <button
                  onClick={() => fetchBundle(true)}
                  disabled={regenerating}
                  className="px-3 py-1.5 bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 border border-purple-500/30 text-purple-100 text-xs font-medium rounded-lg flex items-center space-x-1.5 transition-colors disabled:opacity-60"
                  data-testid="ebook-sales-bundle-regenerate"
                >
                  <ArrowsClockwise size={13} className={regenerating ? 'animate-spin' : ''} />
                  <span>{regenerating ? 'Regenerating…' : 'Regenerate'}</span>
                </button>
                <button
                  onClick={downloadStorefrontZip}
                  disabled={exportingZip}
                  className="px-3 py-1.5 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 hover:from-emerald-500/30 hover:to-teal-500/30 border border-emerald-500/30 text-emerald-100 text-xs font-medium rounded-lg flex items-center space-x-1.5 transition-colors disabled:opacity-60"
                  data-testid="ebook-sales-bundle-export-storefront"
                >
                  <Package size={13} weight="fill" />
                  <span>{exportingZip ? 'Building ZIP…' : 'Download Sales Bundle (.zip)'}</span>
                </button>
              </div>

              {/* Sections */}
              <div className="space-y-3">
                {sections.map((s) => (
                  <div
                    key={s.key}
                    className="bg-white/5 border border-white/10 rounded-xl p-3 sm:p-4"
                    data-testid={`ebook-sales-bundle-section-${s.key}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <h4 className="text-white text-sm font-semibold">{s.label}</h4>
                        {s.hint && <p className="text-slate-500 text-[11px] mt-0.5">{s.hint}</p>}
                      </div>
                      <button
                        onClick={() => copyToClipboard(s.key, s.value || '')}
                        className="flex-shrink-0 px-2.5 py-1 bg-white/10 hover:bg-white/20 text-white text-[11px] font-medium rounded-md flex items-center space-x-1 transition-colors"
                        data-testid={`ebook-sales-bundle-copy-${s.key}`}
                      >
                        <Copy size={11} />
                        <span>{copiedKey === s.key ? 'Copied' : 'Copy'}</span>
                      </button>
                    </div>
                    {s.multiline ? (
                      <pre className="text-slate-200 text-[13px] whitespace-pre-wrap font-sans leading-relaxed">
                        {s.value || '—'}
                      </pre>
                    ) : (
                      <p className="text-slate-200 text-[13px] leading-relaxed break-words">
                        {s.value || '—'}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-slate-500 text-[11px] text-center pt-1">
                Want a different angle? Hit <span className="text-slate-300">Regenerate</span> for a fresh take.
              </p>

              {/* Next-step CTA — keeps the user inside the build loop.
                  Primary: Find Viral Ideas for THIS ebook/business.
                  Secondary: Jump straight into Create Content.
                  Tertiary: Back to Ebook (close modal, stay on result screen). */}
              <div className="bg-gradient-to-br from-purple-500/10 via-fuchsia-500/10 to-pink-500/10 border border-purple-400/30 rounded-xl p-4 mt-4">
                <p className="text-purple-200 text-xs font-semibold uppercase tracking-wider mb-1">
                  Next Recommended Step
                </p>
                <p className="text-white text-sm font-medium mb-3">
                  Find viral content ideas for <span className="text-purple-300">{ebookTitle || 'your ebook'}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      onClose();
                      if (onFindViralIdeas) onFindViralIdeas();
                    }}
                    className="flex-1 min-w-[160px] px-3 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white text-sm font-semibold rounded-lg flex items-center justify-center space-x-1.5 transition-colors"
                    data-testid="ebook-sales-bundle-next-viral-ideas"
                  >
                    <Lightbulb size={14} weight="fill" />
                    <span>Find Viral Ideas</span>
                  </button>
                  <button
                    onClick={() => {
                      onClose();
                      if (onCreateContent) onCreateContent();
                    }}
                    className="flex-1 min-w-[140px] px-3 py-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium rounded-lg flex items-center justify-center space-x-1.5 transition-colors"
                    data-testid="ebook-sales-bundle-next-create-content"
                  >
                    <PencilLine size={14} />
                    <span>Create Content</span>
                  </button>
                  <button
                    onClick={onClose}
                    className="px-3 py-2 bg-white/5 hover:bg-white/10 text-slate-300 text-sm font-medium rounded-lg transition-colors"
                    data-testid="ebook-sales-bundle-back-to-ebook"
                  >
                    Back to Ebook
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
